// RITA-1602 — Form des Handbuchtexts im PRM. Der Text kommt aus der Umwandlung `scripts/handbuch/umwandeln.mjs` (Quelle
// Confluence) und liegt je Handbuch als eigene Datei unter `inhalt/texte/`. Bewusst kein HTML: Die Anzeige baut aus diesen
// Bausteinen React-Elemente, dadurch kann aus dem Text kein Code in die Seite gelangen. Verweise sind reine Sprungziele
// im Handbuch; eine freie Internetadresse gibt es in diesem Modell nicht.

export interface HandbuchZiel {
  handbuch: string;
  kapitel: string;
  abschnitt?: string;
}

export interface Textstueck {
  text: string;
  fett?: boolean;
  kursiv?: boolean;
  code?: boolean;
  ziel?: HandbuchZiel;
}

export interface Zeilenumbruch {
  umbruch: true;
}

export type InlineTeil = Textstueck | Zeilenumbruch;

export interface Absatz {
  typ: 'absatz';
  inhalt: InlineTeil[];
}

export interface Ueberschrift {
  typ: 'ueberschrift';
  ebene: number;
  id: string;
  titel: string;
}

export interface Liste {
  typ: 'liste';
  art: 'nummeriert' | 'punkte';
  start?: number;
  punkte: HandbuchBlock[][];
}

export interface Hinweis {
  typ: 'hinweis';
  art: 'info' | 'warnung' | 'erfolg' | 'fehler';
  bloecke: HandbuchBlock[];
}

export interface Tabelle {
  typ: 'tabelle';
  kopf: HandbuchBlock[][];
  zeilen: HandbuchBlock[][][];
}

export interface Bild {
  typ: 'bild';
  /** Pfad unter `public/handbuch/`. */
  datei: string;
  alt: string;
  breite: number;
  hoehe: number;
  unterschrift?: InlineTeil[];
  /** Anzeigebreite in Pixeln bei kleinen Symbolen; sie werden nicht vergrößert. */
  klein?: number;
}

export type HandbuchBlock = Absatz | Ueberschrift | Liste | Hinweis | Tabelle | Bild;

export interface Kapitelinhalt {
  id: string;
  nummer: number | null;
  titel: string;
  bloecke: HandbuchBlock[];
}

export interface HandbuchInhalt {
  id: string;
  kapitel: Kapitelinhalt[];
}

export function istTextstueck(teil: InlineTeil): teil is Textstueck {
  return 'text' in teil;
}

export function inlineText(teile: readonly InlineTeil[] | undefined): string {
  return (teile ?? []).map((teil) => (istTextstueck(teil) ? teil.text : ' ')).join('');
}

/** Gesamter lesbarer Text eines Bausteins (ohne Bildbeschreibung für Vorleseprogramme); Grundlage der Suche. */
export function blockText(block: HandbuchBlock): string {
  switch (block.typ) {
    case 'absatz':
      return inlineText(block.inhalt);
    case 'ueberschrift':
      return block.titel;
    case 'bild':
      return inlineText(block.unterschrift);
    case 'liste':
      return block.punkte.map((punkt) => punkt.map(blockText).join(' ')).join(' ');
    case 'hinweis':
      return block.bloecke.map(blockText).join(' ');
    case 'tabelle':
      return [...block.kopf, ...block.zeilen.flat()].map((zelle) => zelle.map(blockText).join(' ')).join(' ');
    default:
      return '';
  }
}

export function handbuchBildAdresse(datei: string): string {
  return `${process.env.PUBLIC_URL ?? ''}/handbuch/${datei}`;
}
